import {createAsyncThunk} from '@reduxjs/toolkit';
import type {RootState} from '@/store';
import type {
  AddBillingAddressPayload,
  AddBillingAddressResponse,
  AjaxErrorResponse,
  ApplyDownloadPromoData,
  CanBuyCreditsResponse,
  CheckoutDialogData,
  CheckoutPollingIds,
  CheckoutSessionModeResponse,
  CheckoutSuccessResult,
  CreditPackCheckoutRequest,
  DeleteBillingAddressResponse,
  DownloadCheckoutRequest,
  StripeClientSecretResponse,
  SubscriptionCheckoutRequest,
  UserCurrentSubscriptionStatus,
} from '@Components/checkout-flow/checkout-flow.types';
import {
  CHECKOUT_WEBHOOK_POLLING_TIMEOUT_MS,
  CheckoutMode,
  DEFAULT_TRIAL_SOURCE,
  NO_PROMO_CODE_SENTINEL,
} from '@Components/checkout-flow/checkout-flow.types';
import {
  addBillingAddress as addBillingAddressToState,
  removeBillingAddress,
  setCheckoutDialogData,
  setPollingIds,
  setStripeSession,
} from '@Components/checkout-flow/checkout-flow-reducer';
import {getDialogDataModeForRequest, getTrialSourceForRequest} from '@Components/checkout-flow/checkout-flow.library';
import type {CheckoutRequest, WebhookPollingRequest} from '@Components/checkout-flow/checkout-flow.types';
import {assertNever} from '@Utils/assert-never.util';

interface LoadDialogDataParams {
  request: CheckoutRequest;
}

export const loadCheckoutDialogData = createAsyncThunk<CheckoutDialogData, LoadDialogDataParams>(
  'checkoutFlow/loadDialogData',
  async ({request}, {dispatch}) => {
    const response: unknown = await window.PMW.writeLocal('checkout/getCheckoutDialogData', {
      mode: getDialogDataModeForRequest(request),
      isTrial: request.mode === CheckoutMode.SUBSCRIPTION && request.isTrial ? 1 : 0,
      isDownloadPurchase: request.mode === CheckoutMode.DOWNLOAD ? 1 : 0,
      isCreditPackPurchase: request.mode === CheckoutMode.CREDIT_PACK ? 1 : 0,
    });
    const dialogData = response as CheckoutDialogData;
    dispatch(setCheckoutDialogData(dialogData));
    return dialogData;
  }
);

export const getUserCurrentSubscriptionStatus = async (): Promise<UserCurrentSubscriptionStatus | null> => {
  const response: unknown = await window.PMW.readLocal('premium/getUserCurrentSubscriptionStatus');
  if (response === null || response === undefined || response === false) {
    return null;
  }
  return response as UserCurrentSubscriptionStatus;
};

/**
 * Refuses when eligibility cannot be established: an unverified caller is sent to ask their team admin
 * rather than into a purchase the backend will reject anyway.
 */
export const canCurrentUserBuyCredits = async (): Promise<boolean> => {
  try {
    const response = (await window.PMW.readLocal('checkout/canCurrentUserBuyCredits')) as CanBuyCreditsResponse;
    return response.canBuyCredits;
  } catch {
    return false;
  }
};

export const addCheckoutEmailAddress = async (emailAddress: string): Promise<void> => {
  await window.PMW.writeLocal('user/addEmailAddress', {emailAddress});
};

interface CreateSubscriptionSessionParams {
  request: SubscriptionCheckoutRequest;
}

export const createSubscriptionCheckoutSession = createAsyncThunk<StripeClientSecretResponse, CreateSubscriptionSessionParams>(
  'checkoutFlow/createSubscriptionSession',
  async ({request}, {getState, dispatch}) => {
    const {promo} = (getState() as RootState).checkoutFlow;
    const response = (await window.PMW.writeLocal('checkout/getClientSecretForNewSubscription', {
      priceId: request.priceId,
      isTrial: request.isTrial ? 1 : 0,
      seats: request.seats,
      promoCode: promo.isApplied && promo.code !== '' ? promo.code : NO_PROMO_CODE_SENTINEL,
      source: getTrialSourceForRequest(request, DEFAULT_TRIAL_SOURCE),
    })) as StripeClientSecretResponse;
    dispatch(setStripeSession(response));
    return response;
  }
);

interface CreateOneTimePaymentSessionParams {
  request: CreditPackCheckoutRequest | DownloadCheckoutRequest;
}

export const createOneTimePaymentCheckoutSession = createAsyncThunk<StripeClientSecretResponse, CreateOneTimePaymentSessionParams>(
  'checkoutFlow/createOneTimePaymentSession',
  async ({request}, {getState, dispatch}) => {
    const {selectedCreditPackPriceId} = (getState() as RootState).checkoutFlow;
    const isDownload = request.mode === CheckoutMode.DOWNLOAD;
    const response = (await window.PMW.writeLocal('checkout/getClientSecretForOneTimePayment', {
      priceId: isDownload ? request.priceId : selectedCreditPackPriceId,
      posterHashedId: isDownload ? request.posterHashedId : '',
      cartId: isDownload ? request.cartId : '',
      isSilent: isDownload && request.isSilent ? 1 : 0,
    })) as StripeClientSecretResponse;
    dispatch(setStripeSession(response));
    return response;
  }
);

export const validateSubscriptionPromo = (promoCode: string, request: SubscriptionCheckoutRequest): Promise<void> => {
  return new Promise((resolve, reject) => {
    window.PMW.premiumDialogs.processSubscriptionPromo(
      promoCode,
      request.priceId,
      request.price,
      request.seats,
      () => {
        resolve();
      },
      (errorMessage: string) => {
        reject(new Error(errorMessage));
      }
    );
  });
};

interface ApplyDownloadPromoParams {
  promoCode: string;
  request: DownloadCheckoutRequest;
}

export const applyDownloadPromo = async ({promoCode, request}: ApplyDownloadPromoParams): Promise<ApplyDownloadPromoData> => {
  try {
    const response: unknown = await window.PMW.readLocal('cart/applyPromo', {
      promotioncode: promoCode,
      posterHash: request.posterHashedId,
      idProduct: request.productId,
      cartId: request.cartId,
    });
    return response as ApplyDownloadPromoData;
  } catch (error) {
    throw new Error(getPromoFailureMessage(error), {cause: error});
  }
};

const getPromoFailureMessage = (error: unknown): string => {
  const ajaxError = error as AjaxErrorResponse & {data?: {message?: string}};
  const failMessage = ajaxError.data?.message ?? ajaxError.message ?? '';
  return failMessage !== '' ? failMessage : window.i18next.t('pmwjs_enter_valid_promo');
};

export const addBillingAddress = createAsyncThunk<AddBillingAddressResponse, AddBillingAddressPayload>(
  'checkoutFlow/addBillingAddress',
  async (payload, {dispatch}) => {
    const response = (await window.PMW.writeLocal('user/addAddress', payload)) as AddBillingAddressResponse;
    dispatch(addBillingAddressToState(response.address));
    return response;
  }
);

export const deleteBillingAddress = createAsyncThunk<DeleteBillingAddressResponse, number>(
  'checkoutFlow/deleteBillingAddress',
  async (addressId, {dispatch}) => {
    const response = (await window.PMW.writeLocal('user/deleteAddress', {id: addressId})) as DeleteBillingAddressResponse;
    dispatch(removeBillingAddress(response.addressId));
    return response;
  }
);

export const getCheckoutSessionMode = async (sessionId: string): Promise<CheckoutSessionModeResponse> => {
  return (await window.PMW.readLocal('checkout/getCheckoutModeForSession', {sessionId})) as CheckoutSessionModeResponse;
};

interface FetchPollingIdsParams {
  sessionId: string;
  isTrial: boolean;
}

export const fetchCheckoutPollingIds = createAsyncThunk<CheckoutPollingIds, FetchPollingIdsParams>(
  'checkoutFlow/fetchPollingIds',
  async ({sessionId, isTrial}, {dispatch}) => {
    const uri = isTrial ? 'checkout/processNewTrialSubscription' : 'checkout/getIdsForSession';
    const response = (await window.PMW.readLocal(uri, {sessionId})) as Partial<CheckoutPollingIds>;
    const pollingIds: CheckoutPollingIds = {
      invoiceId: response.invoiceId ?? '',
      chargeId: response.chargeId ?? '',
      paymentIntentId: response.paymentIntentId ?? '',
      setupIntentId: response.setupIntentId ?? '',
    };
    dispatch(setPollingIds(pollingIds));
    return pollingIds;
  }
);

const hasAnyPollingId = (pollingIds: CheckoutPollingIds): boolean => {
  return Object.values(pollingIds).some((id) => id !== '');
};

/**
 * By the time polling starts the flow's mode is the effective purchase mode; PAYMENT_PROCESSING only survives for a
 * Stripe `setup` session (card update), whose completion is confirmed through the setup intent.
 */
const getWebhookPollingRequest = (mode: CheckoutMode, pollingIds: CheckoutPollingIds): WebhookPollingRequest => {
  switch (mode) {
    case CheckoutMode.SUBSCRIPTION:
      return {uri: 'premium/hasSuccessfulSubscriptionPaymentWebhookProcessed', data: {invoiceId: pollingIds.invoiceId}};
    case CheckoutMode.PAYMENT_PROCESSING:
      return {uri: 'premium/confirmManageCardsSetupIntent', data: {setupIntentId: pollingIds.setupIntentId}};
    case CheckoutMode.CREDIT_PACK:
      return {uri: 'premium/hasSuccessfulCreditPackPaymentWebhookProcessed', data: {chargeId: pollingIds.chargeId}};
    case CheckoutMode.DOWNLOAD:
    case CheckoutMode.PREMIUM_CONFIRMATION:
    case CheckoutMode.WELCOME_TO_TEAM:
      return {uri: 'cart/hasSuccessfulDownloadPaymentWebhookProcessed', data: {chargeId: pollingIds.chargeId}};
    default:
      return assertNever(mode);
  }
};

interface PollForCompletionParams {
  mode: CheckoutMode;
  pollingIds: CheckoutPollingIds;
}

export const pollForCheckoutCompletion = async ({mode, pollingIds}: PollForCompletionParams): Promise<CheckoutSuccessResult> => {
  const idsProvider = (): Promise<CheckoutPollingIds> => {
    if (!hasAnyPollingId(pollingIds)) {
      return Promise.reject(new Error('missing checkout ids'));
    }
    return Promise.resolve(pollingIds);
  };
  const {uri, data} = getWebhookPollingRequest(mode, pollingIds);
  const pollWebhook = (): Promise<unknown> => window.PMW.readLocal(uri, data);

  return (await window.PMW.pollingAjaxCallAsync(idsProvider, pollWebhook, CHECKOUT_WEBHOOK_POLLING_TIMEOUT_MS)) as CheckoutSuccessResult;
};
